import { AnimatePresence, motion } from 'framer-motion'
import { useMemo } from 'react'

import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'

import Portal from '../../utils/portals/portal'

export type ModalPresentation = 'fullScreenModal' | 'bottomSheet' | 'default' | 'rightSidebar' | 'leftSidebar'

type DashboardModalProps = {
  children: React.ReactNode
  onClose: () => void
  open: boolean
  presentation?: ModalPresentation
}

const DashboardModal = ({ children, onClose, open, presentation = 'default' }: DashboardModalProps) => {
  const { isMobile } = useBreakpoint()

  const _onClose = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.preventDefault()
    onClose()
  }

  const justifyContent = useMemo(() => {
    if (isMobile) {
      if (presentation === 'fullScreenModal') return 'justify-start items-start'
      if (presentation === 'bottomSheet') return 'justify-end items-end'
      return 'justify-center items-center'
    }
    if (presentation === 'rightSidebar') return 'justify-end items-end'
    if (presentation === 'leftSidebar') return 'justify-start items-center'
    return 'justify-center items-center'
  }, [presentation, isMobile])

  return (
    <Portal>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/70"
            onClick={_onClose}
          >
            <div className={cn('pointer-events-none absolute inset-0 flex', justifyContent)}>
              <div className="pointer-events-auto" onClick={(e) => e.stopPropagation()}>
                {children}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Portal>
  )
}

export default DashboardModal
